import React, { createContext, useContext, useState, useEffect } from 'react';
import { getEquipment, getUnit } from 'api/unity';

// Define the type for an individual menu item
interface MenuItem {
  type: 'note' | 'bubble' | 'preview' | 'chart' | 'widget' | 'doc' | 'measurements' | 'aiChat';
  data: any;
}

// Define the type for a position entry
interface PositionItem {
  position: 'bottomLeft' | 'bottomRight'; // Restrict positions to specific values
  data: MenuItem[];
}

// Define the context type for menu items
interface SelectedItemsContextType {
  minifiedMenuItems: PositionItem[];
  setMinifiedMenuItems: React.Dispatch<React.SetStateAction<PositionItem[]>>;
  addItemToPosition: (position: 'bottomLeft' | 'bottomRight', item: MenuItem) => void;
  removeMinifiedTab: (mode: MenuItem['type'], position: 'left' | 'right') => void;
  selectedTabLeft: number;
  setSelectedTabLeft: React.Dispatch<React.SetStateAction<number>>;
  selectedTabRight: number;
  setSelectedTabRight: React.Dispatch<React.SetStateAction<number>>;
  minifiedMenuOpen: boolean;
  setMinifiedMenuOpen: any;
  menuVisibilityParams: { left: boolean; right: boolean };
  setMenuVisibilityParams: React.Dispatch<React.SetStateAction<{ left: boolean; right: boolean }>>;
}

// Define the context type for image preview data
interface MinifiedImgPreviewData {
  leftPanelId: { fileSrc: string | null, fileName: string; fileType: string; };
  rightPanelId: { fileSrc: string | null, fileName: string; fileType: string; };
}
interface MinifiedImgPreviewContextType {
  minifiedImgPreviewData: MinifiedImgPreviewData;
  updateLeftPanel: (data: Partial<MinifiedImgPreviewData['leftPanelId']>) => void;
  updateRightPanel: (data: Partial<MinifiedImgPreviewData['rightPanelId']>) => void;
}

// Combined context type
type CombinedContextType = SelectedItemsContextType & MinifiedImgPreviewContextType;

// Create the context
const CombinedContext = createContext<CombinedContextType | null>(null);

// Provider Component
export const MinifiedMenuItemsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // State for menu items
  const [minifiedMenuItems, setMinifiedMenuItems] = useState<PositionItem[]>([
    { position: 'bottomLeft', data: [] },
    { position: 'bottomRight', data: [] },
  ]);
  const [selectedTabLeft, setSelectedTabLeft] = useState<number>(0);
  const [selectedTabRight, setSelectedTabRight] = useState<number>(0);

  const [minifiedMenuOpen, setMinifiedMenuOpen] = useState(true);
  const [menuVisibilityParams, setMenuVisibilityParams] = useState({ left: true, right: true });

  // State for image preview data
  const [minifiedImgPreviewData, setMinifiedImgPreviewData] = useState<MinifiedImgPreviewData>({
    leftPanelId: { fileSrc: '', fileName: '', fileType: '' },
    rightPanelId: { fileSrc: '', fileName: '', fileType: '' },
  });

const addItemToPosition = (position: 'bottomLeft' | 'bottomRight', item: MenuItem) => {
  setMinifiedMenuItems((prev) => {
    const updatedMenuItems = prev.map((entry) => {
      // only modify the target side
      if (entry.position === position) {
        const existingIndex = entry.data.findIndex((existingItem) => existingItem.type === item.type);

        // If item of this type already exists, replace it
        if (existingIndex !== -1) {
          const updatedData = [...entry.data];
          updatedData[existingIndex] = item;
          return { ...entry, data: updatedData };
        }

        // If no item of this type yet, add it (but limit to 1 per type)
        return { ...entry, data: [...entry.data, item] };
      }

      // untouched for the opposite side
      return entry;
    });

    // Auto-select the newly added/updated tab on that side
    const targetMenu = updatedMenuItems.find((entry) => entry.position === position);
    if (targetMenu) {
      const lastIndex = targetMenu.data.length - 1;
      if (position === 'bottomLeft') {
        setSelectedTabLeft(lastIndex);
      } else {
        setSelectedTabRight(lastIndex);
      }
    }

    return updatedMenuItems;
  });
};

  const removeMinifiedTab = (mode: MenuItem['type'], position: 'left' | 'right') => {
    const mappedPosition = position === 'left' ? 'bottomLeft' : 'bottomRight';

    setMinifiedMenuItems((prev) =>
      prev.map((entry) =>
        entry.position === mappedPosition
          ? { ...entry, data: entry.data.filter((item) => item.type !== mode) }
          : entry
      )
    );
  };

  const updateLeftPanel = (data: Partial<MinifiedImgPreviewData['leftPanelId']>) => {
    setMinifiedImgPreviewData((prev) => ({
      ...prev,
      leftPanelId: { ...prev.leftPanelId, ...data },
    }));
  };

  const updateRightPanel = (data: Partial<MinifiedImgPreviewData['rightPanelId']>) => {
    setMinifiedImgPreviewData((prev) => ({
      ...prev,
      rightPanelId: { ...prev.rightPanelId, ...data },
    }));
  };

  return (
    <CombinedContext.Provider
      value={{
        minifiedMenuItems,
        setMinifiedMenuItems,
        addItemToPosition,
        removeMinifiedTab,
        minifiedImgPreviewData,
        updateLeftPanel,
        updateRightPanel,
        selectedTabLeft,
        setSelectedTabLeft,
        selectedTabRight,
        setSelectedTabRight,
        minifiedMenuOpen,
        setMinifiedMenuOpen,
        setMenuVisibilityParams,
        menuVisibilityParams
      }}
    >
      {children}
    </CombinedContext.Provider>
  );
};

// Custom Hook
export const useMinifiedMenu = (): CombinedContextType => {
  const context = useContext(CombinedContext);
  if (!context) {
    throw new Error('useMinifiedMenu must be used within a CombinedProvider');
  }
  return context;
};
